import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { User, Contract } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Plus, FileText, Crown, Calendar, DollarSign, Clock } from 'lucide-react';
import { ProfileLink } from '@/components/ProfileLink';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/roles';

interface SubWithContracts extends User {
    is_owned: boolean;
    active_contracts_count: number;
    total_balance: number;
    total_paid: number;
    next_payment_due: string | null;
    contracts: Contract[];
}

export default function MySubsPage() {
    const { data, isLoading } = useQuery({
        queryKey: ['my-subs'],
        queryFn: () => api.get<{ data: SubWithContracts[] }>('/my-subs'),
    });

    const subs = data?.data ?? [];

    const isDueSoon = (date: string | null) => {
        if (!date) return false;
        const diff = new Date(date).getTime() - Date.now();
        return diff >= 0 && diff < 3 * 24 * 60 * 60 * 1000; // within 3 days
    };

    const isOverdue = (date: string | null) => {
        if (!date) return false;
        return new Date(date).getTime() < Date.now();
    };

    return (
        <div className="space-y-6">
            <h1 className="text-xl font-semibold">My Subs</h1>

            {isLoading ? (
                <p className="text-sm text-slate-400">Loading...</p>
            ) : subs.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center">
                        <Users className="mx-auto mb-3 h-10 w-10 text-slate-600" />
                        <p className="text-sm text-slate-400">
                            No subs yet. When subs follow you, request ownership, or sign contracts, they'll appear here.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {subs.map((sub) => (
                        <Card key={sub.id}>
                            <CardContent className="p-4 space-y-4">
                                {/* Sub header */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {sub.avatar_path ? (
                                            <img src={sub.avatar_path} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
                                        ) : (
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-900/50 text-sm font-bold text-violet-300">
                                                {sub.display_name?.[0]?.toUpperCase() ?? '?'}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-medium text-slate-100">
                                                <ProfileLink userId={sub.id} displayName={sub.display_name} className="text-slate-100 hover:text-violet-400 hover:underline" />
                                            </p>
                                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                <Badge className={`text-[10px] ${getRoleBadgeColor(sub.role)}`}>{getRoleLabel(sub.role)}</Badge>
                                                {sub.is_owned && (
                                                    <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">
                                                        <Crown className="mr-0.5 h-2.5 w-2.5" />Owned
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Link to={`/subs/${sub.id}/new-contract`} className="shrink-0">
                                        <Button size="sm" variant="default">
                                            <Plus className="mr-1 h-3.5 w-3.5" />
                                            New Contract
                                        </Button>
                                    </Link>
                                </div>

                                {/* Bio */}
                                {sub.bio && (
                                    <p className="text-xs text-slate-500 line-clamp-1">{sub.bio}</p>
                                )}

                                {/* Stats row */}
                                {sub.active_contracts_count > 0 && (
                                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                                        <span className="flex items-center gap-1 text-slate-400">
                                            <FileText className="h-3 w-3" />
                                            {sub.active_contracts_count} contract{sub.active_contracts_count !== 1 ? 's' : ''}
                                        </span>
                                        <span className="flex items-center gap-1 font-semibold text-red-400">
                                            <DollarSign className="h-3 w-3" />
                                            {formatCurrency(sub.total_balance)} owed
                                        </span>
                                        {sub.total_paid > 0 && (
                                            <span className="flex items-center gap-1 text-green-400">
                                                <DollarSign className="h-3 w-3" />
                                                {formatCurrency(sub.total_paid)} paid
                                            </span>
                                        )}
                                        {sub.next_payment_due && (
                                            <span className={`flex items-center gap-1 ${
                                                isOverdue(sub.next_payment_due)
                                                    ? 'text-red-400 font-semibold'
                                                    : isDueSoon(sub.next_payment_due)
                                                        ? 'text-amber-400'
                                                        : 'text-slate-400'
                                            }`}>
                                                <Calendar className="h-3 w-3" />
                                                {isOverdue(sub.next_payment_due)
                                                    ? `Overdue (${formatDate(sub.next_payment_due)})`
                                                    : `Due ${formatDate(sub.next_payment_due)}`
                                                }
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Contract list */}
                                {sub.active_contracts_count > 0 ? (
                                    <div className="space-y-1.5">
                                        {sub.contracts.map((contract) => (
                                            <Link
                                                key={contract.id}
                                                to={`/contracts/${contract.id}`}
                                                className="flex items-center justify-between gap-2 rounded-md border border-slate-800 bg-slate-800/50 px-3 py-2.5 text-sm transition-colors hover:bg-slate-800"
                                            >
                                                <div className="min-w-0 flex items-center gap-2">
                                                    <span className="truncate text-slate-200">{contract.title}</span>
                                                    {contract.status === 'pending' && (
                                                        <Badge className="bg-amber-500/20 text-amber-400 text-[9px] shrink-0">Pending</Badge>
                                                    )}
                                                    {contract.hardcore_mode && (
                                                        <Badge variant="destructive" className="text-[9px] shrink-0">HC</Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0 text-xs">
                                                    {contract.next_payment_due_date && contract.status === 'active' && (
                                                        <span className={`hidden sm:flex items-center gap-1 ${
                                                            isOverdue(contract.next_payment_due_date)
                                                                ? 'text-red-400'
                                                                : isDueSoon(contract.next_payment_due_date)
                                                                    ? 'text-amber-400'
                                                                    : 'text-slate-500'
                                                        }`}>
                                                            <Clock className="h-3 w-3" />
                                                            {formatDate(contract.next_payment_due_date)}
                                                        </span>
                                                    )}
                                                    <span className="font-semibold text-red-400">
                                                        {formatCurrency(contract.current_balance)}
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500">No active contracts</p>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
